import { watch, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { useViewerModeStore, ViewerMode } from '@/modules/viewer/store/useViewerModeStore';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useUserStore } from '@/store/useUserStore';
import { useCanPersistViewerLayout } from '@/modules/viewer/composables/useViewerModeSwitch';
import { isAvailableViewerMode, isValidViewerMode, resolveViewerMode, useViewerAvailableModes } from '@/modules/viewer/composables/useViewerAvailableModes';

/**
 * Keeps `?mode=` in the URL in sync with `useViewerModeStore.currentMode`
 * in both directions:
 *
 * - On mount: reads `?mode=` from the route and applies it to the store
 *   IF the mode is currently available (e.g. not Logs on mobile).
 *   Otherwise leaves both the store and the query param alone so the deep
 *   link can reactivate later when availability flips on (viewport rotates,
 *   artifact loads async).
 * - On availability change: re-applies the query-param mode if it becomes
 *   available and doesn't already match the current mode.
 * - On mode change: replaces the URL's `mode` query param. Video (the default)
 *   strips the param entirely to keep URLs clean — UNLESS the transition was
 *   caused by auto-fallback (previous mode is no longer available), in which
 *   case the query param is preserved so the deep link survives rotation.
 *
 * Share links automatically carry the param because it's in the URL. Uses
 * `router.replace` to avoid history pollution.
 *
 * Call once near the top of a layout component that owns the viewer route.
 */
export const useViewerModeUrlSync = () => {
  const route = useRoute();
  const router = useRouter();
  const viewerModeStore = useViewerModeStore();
  const { currentMode } = storeToRefs(viewerModeStore);
  const { viewerLayout: savedItemLayout } = storeToRefs(useViewerItemStore());
  const { viewerLayout: userDefaultLayout } = storeToRefs(useUserStore());
  const canPersistViewerLayout = useCanPersistViewerLayout();
  // The owning layout already installs the availability fallback watcher.
  // URL synchronization only needs the reactive mode list for resolution.
  const { availableModes } = useViewerAvailableModes({ syncCurrentMode: false });

  const routeMode = () => {
    const queryMode = route.query.mode;
    return isValidViewerMode(queryMode) ? queryMode : undefined;
  };

  const applyResolvedMode = ({ includeSavedItemLayout = true } = {}) => {
    const mode = resolveViewerMode({
      deepLinkMode: routeMode(),
      savedItemLayout: includeSavedItemLayout ? savedItemLayout.value : undefined,
      userDefaultLayout: userDefaultLayout.value,
      availableModes: availableModes.value,
    });

    if (mode !== currentMode.value) {
      viewerModeStore.setMode(mode);
    }
  };

  onMounted(() => {
    applyResolvedMode();
  });

  // The item's saved layout initializes every viewer, but only edit-capable
  // viewers own that item-level preference after mount. A guest's manual mode
  // is session-only and must survive later availability changes.
  watch([availableModes, userDefaultLayout], () => {
    // A guest's current mode is their session preference after mount. Re-run
    // resolution only for a deep link, which is explicit URL state; otherwise
    // an async artifact must not replace their manual selection with Logs.
    if (!canPersistViewerLayout.value && !routeMode()) {
      return;
    }
    applyResolvedMode({ includeSavedItemLayout: canPersistViewerLayout.value });
  });

  watch(currentMode, (mode, oldMode) => {
    const currentQueryMode = route.query.mode;
    const oldModeUnavailable = oldMode !== undefined && oldMode !== ViewerMode.VIDEO && !isAvailableViewerMode(oldMode, availableModes.value);
    if (oldModeUnavailable && currentQueryMode === oldMode) {
      return;
    }

    if (mode === ViewerMode.VIDEO) {
      // An explicit switch to Video restores the default URL by removing the
      // mode query param. Auto-fallbacks return above and preserve it.
      if (currentQueryMode !== undefined) {
        const query = { ...route.query };
        delete query.mode;
        router.replace({ query });
      }
      return;
    }
    if (currentQueryMode !== mode) {
      router.replace({ query: { ...route.query, mode } });
    }
  });
};
